/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import { cn } from "@/lib/utils";
import React, { ReactNode, useEffect, useRef, useState } from "react";

interface ResizableDivProps {
  width?: number;
  height?: number;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
  children: ReactNode;
  direction?: "x" | "y" | "both";
  onResize: ({ width, height }: { width: number; height: number }) => void;
}

export const ResizeBox = ({
  children,
  onResize,
  width = 100,
  height = 100,
  minWidth = 100,
  maxWidth = 300,
  minHeight = 100,
  maxHeight = 300,
  className,
  direction = "x",
}: ResizableDivProps) => {
  const [size, setSize] = useState({ width, height });
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;

    const container = containerRef.current;
    if (!container) return;

    const newWidth =
      direction !== "y"
        ? e.clientX - container.getBoundingClientRect().left
        : size.width;

    const newHeight =
      direction !== "x"
        ? e.clientY - container.getBoundingClientRect().top
        : size.height;

    setSize((prevSize) => ({
      width:
        direction !== "y" && newWidth >= minWidth && newWidth <= maxWidth
          ? newWidth
          : prevSize.width,
      height:
        direction !== "x" && newHeight >= minHeight && newHeight <= maxHeight
          ? newHeight
          : prevSize.height,
    }));
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    onResize(size);
  }, [size]);

  const ItemWidth = direction === "y" ? "100%" : `${size.width}px`;
  const ItemHeight = direction === "x" ? "100%" : `${size.height}px`;

  return (
    <div
      ref={containerRef}
      className={cn("relative h-full flex items-center", className)}
      style={{ width: ItemWidth, height: ItemHeight }}
    >
      {children}
      <div
        className={cn(
          "absolute z-10 group flex justify-center",
          direction === "x" ? "cursor-col-resize right-0 top-0 h-full w-2" : "",
          direction === "y"
            ? "cursor-row-resize bottom-0 left-0 w-full h-2"
            : "",
          direction === "both"
            ? "cursor-se-resize right-0 bottom-0 w-2 h-2"
            : ""
        )}
        onMouseDown={handleMouseDown}
      >
        <span className="absolute h-full border border-gray-200 group-hover:border-gray-400"></span>
      </div>
    </div>
  );
};
